// Vendor Program v2 PR 2 — shared staff review drawer for a monthly vendor
// report (mounted from the vendor Reports tab and the loan Vendors tab;
// deep-linkable via ?report=<id>). All four sections render labeled: §1/§2
// are shared with the borrower, §3/§4 are clearly marked Private to
// Phoenix. Review actions (Accept / Return-with-comment) render as inline
// confirm sections, the VendorInvoiceDrawer convention for drawer actions.

import { formatCalendarDate, formatPeriodMonth } from "@/design-system/lib/format";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Button, StatusBadge } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { extractErrorMessage } from "@/api/client";
import { ResourceDrawer } from "@/components/portfolioAdmin/loanDetail/common/ResourceDrawer";
import { useDateFormatter } from "@/utils/datetime";
import {
  useAcceptVendorReport,
  useRecordVendorReportSignOff,
  useReturnVendorReport,
  useVendorReportReview,
} from "@/hooks/portfolioAdmin/useVendorReports";
import { fmtMoney } from "@/components/portfolioAdmin/vendors/billing/vendorBillingFormat";
import {
  VENDOR_REPORT_STATUS_LABEL,
  vendorReportStatusTone,
} from "./vendorReportFormat";

type ActionMode = null | "accept" | "return" | "sign-off";

interface VendorReportReviewDrawerProps {
  reportId: string;
  open: boolean;
  onClose: () => void;
}

export function VendorReportReviewDrawer({
  reportId,
  open,
  onClose,
}: VendorReportReviewDrawerProps) {
  const toast = useToast();
  const auth = useAuth();
  const { date } = useDateFormatter();
  const reviewQuery = useVendorReportReview(reportId);

  const acceptMutation = useAcceptVendorReport(reportId);
  const returnMutation = useReturnVendorReport(reportId);
  const signOffMutation = useRecordVendorReportSignOff(reportId);

  const [mode, setMode] = useState<ActionMode>(null);
  const [comment, setComment] = useState("");
  // Same three 1–4 star areas the borrower rates in the portal.
  const [serviceQuality, setServiceQuality] = useState(4);
  const [responsiveness, setResponsiveness] = useState(4);
  const [satisfaction, setSatisfaction] = useState(4);
  const [signOffFeedback, setSignOffFeedback] = useState("");

  const review = reviewQuery.data;
  const report = review?.report;

  const canManage =
    auth.hasPermission("Vendors.Manage") || auth.hasRoleCode("ADMIN");
  const canAct = canManage && report?.status === "submitted";
  // Sign-off is only reachable from accepted, exactly as in the portal. Before
  // this the admin drawer offered no sign-off action at all, so a report whose
  // borrower never logged in sat at accepted forever.
  const canRecordSignOff =
    canManage && report?.status === "accepted" && !review?.signOff;

  // Period boundaries are wall-calendar months stored at UTC midnight —
  // rendering them through the tz-aware formatter shifted June into May.
  const month = (iso: string) => formatPeriodMonth(iso, "short");

  const resetForms = () => {
    setMode(null);
    setComment("");
    setServiceQuality(4);
    setResponsiveness(4);
    setSatisfaction(4);
    setSignOffFeedback("");
  };

  const onAccept = async () => {
    try {
      await acceptMutation.mutateAsync();
      toast.success("Report accepted — the borrower can now sign off.");
      resetForms();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const onReturn = async () => {
    try {
      await returnMutation.mutateAsync({ comment: comment.trim() });
      toast.success("Report returned to the vendor for correction.");
      resetForms();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const onRecordSignOff = async () => {
    try {
      await signOffMutation.mutateAsync({
        serviceQuality,
        responsiveness,
        satisfaction,
        feedback: signOffFeedback.trim() || null,
      });
      toast.success("Sign-off recorded on the borrower's behalf.");
      resetForms();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      width="lg"
      title={report ? `${month(report.periodStart)} report` : "Vendor report"}
      subtitle={
        report
          ? `${report.vendorName} · ${report.loanNumber} · ${report.roleOnLoan}`
          : undefined
      }
      testId="vendor-report-review-drawer"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Close</Button>
          {canAct && (
            <>
              <Button
                variant="secondary"
                onClick={() => setMode(mode === "return" ? null : "return")}
                data-testid="report-return-button"
              >
                Return for correction
              </Button>
              <Button
                variant="primary"
                onClick={() => setMode(mode === "accept" ? null : "accept")}
                data-testid="report-accept-button"
              >
                Accept
              </Button>
            </>
          )}
          {canRecordSignOff && (
            <Button
              variant="primary"
              onClick={() => setMode(mode === "sign-off" ? null : "sign-off")}
              data-testid="report-record-signoff-button"
            >
              Record borrower sign-off
            </Button>
          )}
        </>
      }
    >
      {reviewQuery.isLoading && (
        <p className="text-small text-gray-mid italic m-0">Loading report…</p>
      )}
      {reviewQuery.isError && (
        <p className="text-small text-status-error m-0">
          Could not load the report. Refresh to retry.
        </p>
      )}
      {report && (
        <div className="flex flex-col gap-space-4">
          <section>
            <div className="flex items-center justify-between gap-space-2 flex-wrap">
              <div className="flex items-center gap-space-2">
                <StatusBadge tone={vendorReportStatusTone(report.status)}>
                  {VENDOR_REPORT_STATUS_LABEL[report.status]}
                </StatusBadge>
                {report.revisionNumber > 1 && (
                  <StatusBadge
                    tone="neutral"
                    hint={`Revision ${report.revisionNumber} — the vendor resubmitted after a return.`}
                  >
                    Resubmitted (rev {report.revisionNumber})
                  </StatusBadge>
                )}
              </div>
              {/* The explicit range, so the month label is never the only
                  source of truth. Wall-calendar dates: rendered through the
                  tz-aware formatter this read "31 May – 29 Jun" for what is
                  actually 1–30 June. */}
              <span className="text-caption text-gray-mid">
                Period {formatCalendarDate(report.periodStart)} –{" "}
                {formatCalendarDate(report.periodEnd)}
              </span>
            </div>
            {report.submittedAt && (
              <p className="text-caption text-gray-mid m-0 mt-space-1">
                Submitted {date(report.submittedAt)}
                {report.acceptedAt ? ` · Accepted ${date(report.acceptedAt)}` : ""}
                {report.signedOffAt ? ` · Signed off ${date(report.signedOffAt)}` : ""}
              </p>
            )}
            {report.latestReviewComment && (
              <p className="text-small m-0 mt-space-2 rounded-md bg-white-warm p-space-2">
                <span className="text-caption text-gray-mid uppercase tracking-label">
                  Latest review comment
                </span>
                <br />
                {report.latestReviewComment}
              </p>
            )}
          </section>

          <SectionBlock
            heading="§1 Monthly narrative"
            audience="shared"
            testId="report-section-1"
          >
            {report.publicNarrative ? (
              <p className="text-small m-0 whitespace-pre-wrap">
                {report.publicNarrative}
              </p>
            ) : (
              <p className="text-small text-gray-mid italic m-0">
                No narrative provided.
              </p>
            )}
          </SectionBlock>

          <SectionBlock
            heading="§2 Goal grades"
            audience="shared"
            testId="report-section-2"
          >
            {report.goalGrades.length === 0 ? (
              <p className="text-small text-gray-mid italic m-0">
                No goals graded this period.
              </p>
            ) : (
              <table className="w-full text-small border-collapse">
                <thead>
                  <tr className="text-left text-caption text-gray-mid uppercase tracking-label">
                    <th className="font-medium pb-space-1">Goal</th>
                    <th className="font-medium pb-space-1 text-right">Score</th>
                    <th className="font-medium pb-space-1">Complete</th>
                    <th className="font-medium pb-space-1">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {report.goalGrades.map((g) => (
                    <tr
                      key={g.goalId}
                      className="border-t border-border-light align-top"
                    >
                      <td className="py-space-1 pr-space-2">{g.goalTitle}</td>
                      {/* Goal scores are 0–100 (clamped server-side in
                          VendorMonthlyReportService). This borrowed the 1–4
                          star denominator used further down for the sign-off
                          ratings — same number, wrong scale. The portal has
                          always shown /100 for this field. */}
                      <td className="py-space-1 text-right tabular-nums">
                        {g.score} / 100
                      </td>
                      <td className="py-space-1">{g.isComplete ? "Yes" : "No"}</td>
                      <td className="py-space-1 text-gray-mid">{g.note ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </SectionBlock>

          <SectionBlock
            heading="§3 Concerns"
            audience="phoenix"
            testId="report-section-3"
          >
            {report.privateConcerns ? (
              <p className="text-small m-0 whitespace-pre-wrap">
                {report.privateConcerns}
              </p>
            ) : (
              <p className="text-small text-gray-mid italic m-0">
                No concerns raised.
              </p>
            )}
          </SectionBlock>

          <SectionBlock
            heading={
              report.isRegulatedVendor
                ? "§4 Collected from the borrower"
                : "§4 Self-reported invoice amount"
            }
            audience="phoenix"
            testId="report-section-4"
          >
            {report.isRegulatedVendor ? (
              // F11 regulated (borrower-paid) — Phoenix never invoices this
              // vendor; §4 is what they collected from the borrower.
              (report.collectedLineItems ?? []).length > 0 ? (
                <table
                  className="w-full text-small border-collapse"
                  data-testid="report-collected-lines"
                >
                  <thead>
                    <tr className="text-left text-caption text-gray-mid uppercase tracking-label">
                      <th className="font-medium pb-space-1">Collected for</th>
                      <th className="font-medium pb-space-1 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.collectedLineItems!.map((line) => (
                      <tr key={line.id} className="border-t border-border-light align-top">
                        <td className="py-space-1 pr-space-2">{line.serviceDescription}</td>
                        <td className="py-space-1 text-right tabular-nums">
                          {fmtMoney(line.amount)}
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t border-border-light font-medium">
                      <td className="py-space-1 pr-space-2">Total collected</td>
                      <td className="py-space-1 text-right tabular-nums font-display font-bold">
                        {fmtMoney(
                          report.collectedLineItems!.reduce((sum, l) => sum + l.amount, 0),
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              ) : (
                <p
                  className="text-small text-gray-mid italic m-0"
                  data-testid="report-collected-empty"
                >
                  {report.collectedNothingConfirmed
                    ? "Nothing collected this month (confirmed by the vendor)."
                    : "No collected amounts reported."}
                </p>
              )
            ) : (
              <>
                <div className="flex items-baseline justify-between gap-space-2">
                  <span className="text-caption text-gray-mid uppercase tracking-label">
                    Amount this period
                  </span>
                  <span className="text-right tabular-nums font-display font-bold text-h4">
                    {report.selfReportedInvoiceAmount != null
                      ? fmtMoney(report.selfReportedInvoiceAmount)
                      : "—"}
                  </span>
                </div>
                {report.amountCalculationExplanation && (
                  <p className="text-small m-0 mt-space-1 whitespace-pre-wrap">
                    {report.amountCalculationExplanation}
                  </p>
                )}
              </>
            )}
            {review!.priorAmounts.length > 0 && (
              <div className="mt-space-2" data-testid="report-prior-amounts">
                <span className="text-caption text-gray-mid uppercase tracking-label">
                  {report.isRegulatedVendor
                    ? "Prior collected totals"
                    : "Prior accepted amounts"}
                </span>
                <ul className="m-0 mt-space-1 p-0 list-none flex flex-col gap-space-1">
                  {review!.priorAmounts.map((p) => (
                    <li
                      key={p.periodStart}
                      className="flex items-baseline justify-between gap-space-2 text-small border-b border-border-light pb-space-1"
                    >
                      <span>{month(p.periodStart)}</span>
                      <span className="text-right tabular-nums">
                        {p.amount != null ? fmtMoney(p.amount) : "—"}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </SectionBlock>

          {review!.signOff && (
            <section data-testid="report-signoff">
              <div className="flex items-center gap-space-2 flex-wrap mb-space-2">
                <h3 className="font-display font-semibold text-h4 m-0">
                  Borrower sign-off
                </h3>
                {/* The rating is the borrower's either way — this says who
                    entered it, so a transcribed sign-off is never read as the
                    borrower's own submission. */}
                {review!.signOff.isStaffRecorded && (
                  <StatusBadge
                    tone="info"
                    hint="The borrower gave this rating out of band and Phoenix recorded it on their behalf."
                    data-testid="report-signoff-staff-recorded"
                  >
                    Recorded by Phoenix
                  </StatusBadge>
                )}
              </div>
              {review!.signOff.isStaffRecorded && (
                <p
                  className="text-caption text-gray-mid m-0 mb-space-2"
                  data-testid="report-signoff-recorded-by"
                >
                  Recorded by {review!.signOff.recordedByName ?? "a staff member"} on
                  the borrower&apos;s behalf.
                </p>
              )}
              <dl className="grid grid-cols-2 gap-y-space-1 text-small m-0">
                <dt className="text-caption text-gray-mid uppercase tracking-label">Signed</dt>
                <dd className="m-0">{date(review!.signOff.signedAt)}</dd>
                <dt className="text-caption text-gray-mid uppercase tracking-label">Overall</dt>
                <dd className="m-0 tabular-nums">
                  {review!.signOff.overallRating.toFixed(1)} / 4
                </dd>
                <dt className="text-caption text-gray-mid uppercase tracking-label">Service quality</dt>
                <dd className="m-0 tabular-nums">{review!.signOff.serviceQuality} / 4</dd>
                <dt className="text-caption text-gray-mid uppercase tracking-label">Responsiveness</dt>
                <dd className="m-0 tabular-nums">{review!.signOff.responsiveness} / 4</dd>
                <dt className="text-caption text-gray-mid uppercase tracking-label">Satisfaction</dt>
                <dd className="m-0 tabular-nums">{review!.signOff.satisfaction} / 4</dd>
                {review!.signOff.feedback && (
                  <>
                    <dt className="text-caption text-gray-mid uppercase tracking-label">Feedback</dt>
                    <dd className="m-0 whitespace-pre-wrap">{review!.signOff.feedback}</dd>
                  </>
                )}
              </dl>
            </section>
          )}

          {review!.revisions.length > 0 && (
            <section data-testid="report-revision-trail">
              <h3 className="font-display font-semibold text-h4 m-0 mb-space-2">
                Revision trail
              </h3>
              <ul className="m-0 p-0 list-none flex flex-col gap-space-1">
                {review!.revisions.map((r) => (
                  <li
                    key={r.revisionNumber}
                    className="text-small border-b border-border-light pb-space-1"
                  >
                    <span className="font-medium">Rev {r.revisionNumber}</span>
                    {" · "}submitted {date(r.submittedAt)}
                    {r.reviewedAt && r.reviewOutcome
                      ? ` · ${r.reviewOutcome} ${date(r.reviewedAt)}`
                      : ""}
                    {r.reviewComment && (
                      <span className="block text-gray-mid">
                        “{r.reviewComment}”
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section>
            <p className="text-caption text-gray-mid m-0">
              <Link
                to={`/portfolio-admin/loans/${report.loanId}?tab=vendors`}
                className="text-gold-shadow hover:underline"
              >
                View loan {report.loanNumber}
              </Link>
              {" · "}
              <Link
                to={`/portfolio-admin/vendors/${report.vendorId}?tab=reports`}
                className="text-gold-shadow hover:underline"
              >
                View vendor {report.vendorName}
              </Link>
            </p>
          </section>

          {mode === "accept" && (
            <section className="rounded-md bg-white-warm p-space-3 flex flex-col gap-space-2">
              <h4 className="font-display font-semibold text-small m-0">
                Accept report
              </h4>
              <p className="text-caption text-gray-mid m-0">
                Accepting publishes §1 and §2 to the borrower for review and
                sign-off. §3 and §4 stay private to Phoenix.
              </p>
              <div className="flex justify-end gap-space-2">
                <Button variant="secondary" size="sm" onClick={resetForms}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => void onAccept()}
                  disabled={acceptMutation.isPending}
                  data-testid="report-accept-confirm"
                >
                  Accept report
                </Button>
              </div>
            </section>
          )}

          {mode === "sign-off" && (
            <section
              className="rounded-md bg-white-warm p-space-3 flex flex-col gap-space-2"
              data-testid="report-record-signoff-form"
            >
              <h4 className="font-display font-semibold text-small m-0">
                Record the borrower&apos;s sign-off
              </h4>
              <p className="text-caption text-gray-mid m-0">
                Use this only for a rating the borrower actually gave you — by
                phone, email or in person. It is recorded in their name and
                counts toward this vendor&apos;s average, and every surface
                that shows it will say Phoenix entered it.
              </p>
              {(
                [
                  ["Service quality", serviceQuality, setServiceQuality, "service-quality"],
                  ["Responsiveness", responsiveness, setResponsiveness, "responsiveness"],
                  ["Satisfaction", satisfaction, setSatisfaction, "satisfaction"],
                ] as const
              ).map(([label, value, setValue, testId]) => (
                <label key={testId} className="flex flex-col gap-space-1 text-caption">
                  <span className="text-gray-mid uppercase tracking-label">
                    {label}
                  </span>
                  <select
                    className="h-9 px-space-2 border border-border-light rounded-md text-small bg-white-pure max-w-[200px]"
                    value={value}
                    onChange={(e) => setValue(Number(e.target.value))}
                    data-testid={`report-signoff-${testId}`}
                  >
                    {[1, 2, 3, 4].map((n) => (
                      <option key={n} value={n}>
                        {n} {n === 1 ? "star" : "stars"}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
              <label className="flex flex-col gap-space-1 text-caption">
                <span className="text-gray-mid uppercase tracking-label">
                  Borrower feedback (optional)
                </span>
                <textarea
                  className="min-h-[4rem] px-space-2 py-space-1 border border-border-light rounded-md text-small"
                  value={signOffFeedback}
                  onChange={(e) => setSignOffFeedback(e.target.value)}
                  data-testid="report-signoff-feedback"
                />
              </label>
              <div className="flex justify-end gap-space-2">
                <Button variant="secondary" size="sm" onClick={resetForms}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => void onRecordSignOff()}
                  disabled={signOffMutation.isPending}
                  data-testid="report-record-signoff-confirm"
                >
                  {signOffMutation.isPending ? "Recording…" : "Record sign-off"}
                </Button>
              </div>
            </section>
          )}

          {mode === "return" && (
            <section className="rounded-md bg-white-warm p-space-3 flex flex-col gap-space-2">
              <h4 className="font-display font-semibold text-small m-0">
                Return for correction
              </h4>
              <p className="text-caption text-gray-mid m-0">
                The vendor sees your comment and can revise and resubmit the
                report.
              </p>
              <label className="flex flex-col gap-space-1 text-caption">
                <span className="text-gray-mid uppercase tracking-label">
                  Comment (required)
                </span>
                <textarea
                  className="min-h-[3.5rem] px-space-2 py-space-1 border border-border-light rounded-md text-small"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  data-testid="report-return-comment"
                />
              </label>
              <div className="flex justify-end gap-space-2">
                <Button variant="secondary" size="sm" onClick={resetForms}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => void onReturn()}
                  disabled={!comment.trim() || returnMutation.isPending}
                  data-testid="report-return-confirm"
                >
                  Return report
                </Button>
              </div>
            </section>
          )}
        </div>
      )}
    </ResourceDrawer>
  );
}

function SectionBlock({
  heading,
  audience,
  testId,
  children,
}: {
  heading: string;
  audience: "shared" | "phoenix";
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <section data-testid={testId}>
      <div className="flex items-center gap-space-2 mb-space-2">
        <h3 className="font-display font-semibold text-h4 m-0">{heading}</h3>
        {audience === "shared" ? (
          <StatusBadge
            tone="neutral"
            hint="The borrower sees this section once the report is accepted."
          >
            Shared with borrower
          </StatusBadge>
        ) : (
          <StatusBadge
            tone="onyx"
            hint="Staff-only — never shown to the borrower or other vendors."
          >
            Private to Phoenix
          </StatusBadge>
        )}
      </div>
      {children}
    </section>
  );
}
